import { useState, useEffect, useCallback } from 'react';
import { BarChart3, RefreshCw } from 'lucide-react';
import { GlassCard } from '@/components/custom/GlassCard';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import {
  fetchValidationReport,
  type StatsPeriod, type ValidationReport, type StatsBucket,
} from '@/hooks/useValidationStats';

// « Rythme de validation » — page de pilotage ADMIN (demande user 2026-08-03).
// Labels en français comme les autres écrans d'administration (Journal d'actions, Registre JVID).
// Graphiques en SVG maison : aucune librairie ajoutée, couleurs des pastilles de statut existantes.
//
// RAPPEL DE DÉFINITION (affiché aussi en bas de page) : « traité » = VERDICT FINAL SEUL (Validée ou
// Rejetée). Une escalade, un envoi vers Build ou une question à l'opérateur sont du travail EN COURS :
// ils apparaissent dans l'histogramme du rythme, jamais dans le compteur des traitées.

const COLORS = {
  validated: '#10b981',   // emerald-500 — pastille VALIDATED
  rejected: '#f43f5e',    // rose-500    — pastille REJECTED
  escalated: '#f97316',   // orange-500  — pastille ESCALATED
  sentToBuild: '#0ea5e9', // sky-500     — pastille WAITING_BUILD
  created: '#94a3b8',     // slate-400   — les entrées (tickets reçus)
};

const PERIODS: { key: StatsPeriod; label: string }[] = [
  { key: '7d', label: '7 jours' },
  { key: '30d', label: '30 jours' },
  { key: 'month', label: 'Ce mois' },
  { key: 'all', label: 'Depuis le début' },
];

// La période choisie survit à la navigation (même principe que la vue de la file).
const PERIOD_STORAGE_KEY = 'addressval-stats-period';
function initialPeriod(): StatsPeriod {
  try {
    const saved = localStorage.getItem(PERIOD_STORAGE_KEY);
    if (PERIODS.some((p) => p.key === saved)) return saved as StatsPeriod;
  } catch { /* storage indisponible → défaut */ }
  return '30d';
}

// ── Échelle « ronde » : au plus 5 graduations, toujours entières (ce sont des comptes) ──
function niceScale(rawMax: number): { max: number; ticks: number[] } {
  const v = Math.max(rawMax, 1);
  const pow = Math.max(1, Math.pow(10, Math.floor(Math.log10(v))));
  const step = [1, 2, 5, 10].map((m) => m * pow).find((s) => v / s <= 4) ?? 10 * pow;
  const max = Math.ceil(v / step) * step;
  const ticks: number[] = [];
  for (let t = 0; t <= max + 1e-9; t += step) ticks.push(t);
  return { max, ticks };
}

function shortLabel(bucket: string) {
  const [, m, d] = bucket.split('-');
  return `${d}/${m}`;
}

function longLabel(bucket: string, mode: 'day' | 'week') {
  const txt = new Date(`${bucket}T00:00:00Z`).toLocaleDateString('fr-BE', {
    day: '2-digit', month: 'long', year: 'numeric', timeZone: 'UTC',
  });
  return mode === 'week' ? `Semaine du ${txt}` : txt;
}

const SVG_W = 720;
const PAD = { l: 38, r: 12, t: 12, b: 30 };

/** Grille + graduations + libellés d'axe, partagés par les deux graphiques. */
function ChartFrame({
  height, ticks, max, buckets, children,
}: {
  height: number; ticks: number[]; max: number;
  buckets: string[]; children: React.ReactNode;
}) {
  const plotH = height - PAD.t - PAD.b;
  const plotW = SVG_W - PAD.l - PAD.r;
  const band = plotW / Math.max(buckets.length, 1);
  // Un libellé sur N pour que l'axe reste lisible quel que soit le nombre de points.
  const every = Math.ceil(buckets.length / 12);
  return (
    <svg viewBox={`0 0 ${SVG_W} ${height}`} className="w-full" role="img">
      {ticks.map((t) => {
        const y = PAD.t + plotH - (t / max) * plotH;
        return (
          <g key={t}>
            <line x1={PAD.l} x2={SVG_W - PAD.r} y1={y} y2={y} stroke="#e2e8f0" strokeWidth={1} />
            <text x={PAD.l - 6} y={y + 3.5} textAnchor="end" fontSize={10} fill="#94a3b8">{t}</text>
          </g>
        );
      })}
      {children}
      {buckets.map((b, i) => (
        (i % every === 0 || i === buckets.length - 1) ? (
          <text
            key={b}
            x={PAD.l + band * i + band / 2}
            y={height - PAD.b + 15}
            textAnchor="middle"
            fontSize={10}
            fill="#94a3b8"
          >
            {shortLabel(b)}
          </text>
        ) : null
      ))}
    </svg>
  );
}

/** Histogramme du rythme, empilé par type d'action. */
function RhythmChart({ data, mode }: { data: StatsBucket[]; mode: 'day' | 'week' }) {
  const H = 240;
  const plotH = H - PAD.t - PAD.b;
  const plotW = SVG_W - PAD.l - PAD.r;
  const band = plotW / Math.max(data.length, 1);
  const barW = Math.max(2, Math.min(band * 0.68, 44));
  const { max, ticks } = niceScale(
    Math.max(0, ...data.map((d) => d.validated + d.rejected + d.escalated + d.sentToBuild)),
  );
  const layers: { key: 'validated' | 'rejected' | 'escalated' | 'sentToBuild'; label: string }[] = [
    { key: 'validated', label: 'Validées' },
    { key: 'rejected', label: 'Rejetées' },
    { key: 'escalated', label: 'Escaladées' },
    { key: 'sentToBuild', label: 'Envoyées vers Build' },
  ];
  return (
    <ChartFrame height={H} ticks={ticks} max={max} buckets={data.map((d) => d.bucket)}>
      {data.map((d, i) => {
        const x = PAD.l + band * i + (band - barW) / 2;
        let acc = 0;
        return (
          <g key={d.bucket}>
            {layers.map((l) => {
              const v = d[l.key];
              if (v <= 0) return null;
              const h = (v / max) * plotH;
              const y = PAD.t + plotH - ((acc + v) / max) * plotH;
              acc += v;
              return (
                <rect key={l.key} x={x} y={y} width={barW} height={h} fill={COLORS[l.key]}>
                  <title>{`${longLabel(d.bucket, mode)}\n${l.label} : ${v}`}</title>
                </rect>
              );
            })}
            {/* Zone de survol pleine hauteur : le récapitulatif du jour reste lisible même sur une barre basse. */}
            <rect x={PAD.l + band * i} y={PAD.t} width={band} height={plotH} fill="transparent">
              <title>
                {`${longLabel(d.bucket, mode)}\nTraitées : ${d.validated + d.rejected} `
                  + `(${d.validated} validées, ${d.rejected} rejetées)\n`
                  + `Escaladées : ${d.escalated}\nEnvoyées vers Build : ${d.sentToBuild}`}
              </title>
            </rect>
          </g>
        );
      })}
    </ChartFrame>
  );
}

/** Entrées (tickets reçus) vs sorties (verdicts) — la file monte ou descend ? */
function FlowChart({ data, mode }: { data: StatsBucket[]; mode: 'day' | 'week' }) {
  const H = 220;
  const plotH = H - PAD.t - PAD.b;
  const plotW = SVG_W - PAD.l - PAD.r;
  const band = plotW / Math.max(data.length, 1);
  const outs = data.map((d) => d.validated + d.rejected);
  const { max, ticks } = niceScale(Math.max(0, ...data.map((d) => d.created), ...outs));
  const px = (i: number) => PAD.l + band * i + band / 2;
  const py = (v: number) => PAD.t + plotH - (v / max) * plotH;
  const path = (vals: number[]) => vals.map((v, i) => `${px(i)},${py(v)}`).join(' ');
  return (
    <ChartFrame height={H} ticks={ticks} max={max} buckets={data.map((d) => d.bucket)}>
      <polyline points={path(data.map((d) => d.created))} fill="none" stroke={COLORS.created} strokeWidth={2} />
      <polyline points={path(outs)} fill="none" stroke={COLORS.validated} strokeWidth={2} />
      {data.map((d, i) => (
        <g key={d.bucket}>
          <circle cx={px(i)} cy={py(d.created)} r={2.5} fill={COLORS.created} />
          <circle cx={px(i)} cy={py(outs[i])} r={2.5} fill={COLORS.validated} />
          <rect x={PAD.l + band * i} y={PAD.t} width={band} height={plotH} fill="transparent">
            <title>{`${longLabel(d.bucket, mode)}\nEntrées : ${d.created}\nSorties (verdicts) : ${outs[i]}`}</title>
          </rect>
        </g>
      ))}
    </ChartFrame>
  );
}

function Legend({ items }: { items: { color: string; label: string }[] }) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
      {items.map((it) => (
        <span key={it.label} className="inline-flex items-center gap-1.5 text-[11px] text-slate-500">
          <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: it.color }} />
          {it.label}
        </span>
      ))}
    </div>
  );
}

function Tile({ value, label, hint, cls }: { value: string; label: string; hint?: string; cls?: string }) {
  return (
    <div className="rounded-xl border border-slate-100 bg-white px-4 py-3">
      <p className={cn('text-3xl font-bold leading-tight text-slate-800', cls)}>{value}</p>
      <p className="mt-1 text-xs font-semibold text-slate-600">{label}</p>
      {hint && <p className="mt-0.5 text-[11px] text-slate-400">{hint}</p>}
    </div>
  );
}

export function ValidationStats() {
  const [period, setPeriod] = useState<StatsPeriod>(initialPeriod);
  const [report, setReport] = useState<ValidationReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    try { localStorage.setItem(PERIOD_STORAGE_KEY, period); } catch { /* best-effort */ }
  }, [period]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await fetchValidationReport(period);
      setReport(data);
    } catch (err: any) {
      setError(err?.message || 'Échec du chargement des statistiques');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { void load(); }, [load]);

  const periodLabel = PERIODS.find((p) => p.key === period)?.label ?? '';
  const p = report?.progress;

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-brand-gradient flex items-center justify-center">
            <BarChart3 className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-800">Statistiques</h2>
            <p className="text-sm text-slate-500">
              Rythme de validation : ce qui a été traité, ce qui reste, et à quelle vitesse la file descend.
            </p>
          </div>
        </div>
        <Button onClick={load} disabled={loading} variant="outline" className="shrink-0">
          <RefreshCw className={cn('w-4 h-4 mr-1.5', loading && 'animate-spin')} /> Rafraîchir
        </Button>
      </div>

      {/* Sélecteur de période */}
      <div className="flex flex-wrap items-center gap-2">
        {PERIODS.map((it) => (
          <button
            key={it.key}
            type="button"
            onClick={() => setPeriod(it.key)}
            aria-pressed={period === it.key}
            className={cn(
              'rounded-full border px-4 py-1.5 text-sm font-semibold transition-colors',
              period === it.key
                ? 'border-brand-rose bg-brand-rose/[0.07] text-brand-rose'
                : 'border-slate-200 bg-white text-slate-600 hover:border-brand-rose/40 hover:text-brand-rose',
            )}
          >
            {it.label}
          </button>
        ))}
      </div>

      {error ? (
        <GlassCard className="p-6"><p className="text-sm text-red-600">{error}</p></GlassCard>
      ) : loading && !report ? (
        <div className="space-y-3">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      ) : report && p ? (
        <>
          {/* Avancement global (état actuel, toutes périodes confondues) */}
          <GlassCard className="p-5">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <p className="text-sm font-semibold text-slate-700">
                Avancement global — <span className="text-slate-900">{p.treated}</span>
                <span className="text-slate-400"> / {p.total} dossiers</span>
              </p>
              <p className="text-sm font-bold text-emerald-700">{p.pct} %</p>
            </div>
            <div className="mt-2 h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
              <div
                className="h-full rounded-full bg-emerald-500 transition-[width] duration-500"
                style={{ width: `${Math.min(100, p.pct)}%` }}
              />
            </div>
            <p className="mt-2 text-xs text-slate-500">
              {p.open} encore à traiter · +{p.monthToDate} ce mois · +{p.weekToDate} cette semaine
            </p>
          </GlassCard>

          {/* 4 tuiles KPI */}
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Tile
              value={String(report.totals.treated)}
              label={`Traitées — ${periodLabel.toLowerCase()}`}
              hint={`${report.totals.validated} validées · ${report.totals.rejected} rejetées`}
              cls="text-emerald-700"
            />
            <Tile
              value={`${p.pct} %`}
              label="De la charge totale traitée"
              hint={`${p.treated} sur ${p.total} dossiers`}
            />
            <Tile
              value={String(report.queue.pending)}
              label="En file (en attente)"
              hint={`+ ${report.queue.escalated} escaladés · ${report.queue.waitingBuild} chez Build · ${report.queue.noJvid} sans JVID`}
              cls="text-amber-700"
            />
            <Tile
              value={report.leadTime.avgDays != null ? `${report.leadTime.avgDays} j` : '—'}
              label="Délai moyen création → verdict"
              hint={report.leadTime.medianDays != null
                ? `médiane ${report.leadTime.medianDays} j · sur ${report.leadTime.count} dossiers`
                : 'aucun verdict sur la période'}
            />
          </div>

          {/* Histogramme du rythme */}
          <GlassCard className="p-5">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Rythme {report.bucket === 'week' ? 'par semaine' : 'par jour'}
              </p>
              <Legend items={[
                { color: COLORS.validated, label: 'Validées' },
                { color: COLORS.rejected, label: 'Rejetées' },
                { color: COLORS.escalated, label: 'Escaladées' },
                { color: COLORS.sentToBuild, label: 'Envoyées vers Build' },
              ]} />
            </div>
            <RhythmChart data={report.series} mode={report.bucket} />
            <p className="mt-1 text-[11px] text-slate-400">
              Seules les barres vertes et roses comptent comme « traitées » : une escalade ou un envoi
              vers Build est du travail en cours, pas un verdict.
            </p>
          </GlassCard>

          {/* Entrées vs sorties */}
          <GlassCard className="p-5">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Entrées vs sorties
              </p>
              <Legend items={[
                { color: COLORS.created, label: 'Entrées (tickets reçus)' },
                { color: COLORS.validated, label: 'Sorties (verdicts)' },
              ]} />
            </div>
            <FlowChart data={report.series} mode={report.bucket} />
            <p className="mt-1 text-[11px] text-slate-400">
              Sur la période : {report.totals.created} tickets reçus pour {report.totals.treated} verdicts
              {report.totals.created > report.totals.treated
                ? ' — la file monte.'
                : report.totals.created < report.totals.treated
                  ? ' — la file descend.'
                  : ' — la file est stable.'}
            </p>
          </GlassCard>

          {/* Répartition par validateur */}
          <GlassCard className="p-5">
            <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Par validateur — {periodLabel.toLowerCase()}
            </p>
            {report.byReviewer.length === 0 ? (
              <p className="py-6 text-center text-sm text-slate-400">Aucune activité sur la période.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400">
                    <th className="py-1.5 pr-4 font-semibold">Validateur</th>
                    <th className="py-1.5 pr-4 font-semibold">Traitées</th>
                    <th className="py-1.5 pr-4 font-semibold">Validées</th>
                    <th className="py-1.5 pr-4 font-semibold">Rejetées</th>
                    <th className="py-1.5 pr-4 font-semibold">Escaladées</th>
                    <th className="py-1.5 pr-4 font-semibold">Build</th>
                    <th className="w-[34%] py-1.5 font-semibold" />
                  </tr>
                </thead>
                <tbody>
                  {report.byReviewer.map((r) => {
                    const top = Math.max(1, report.byReviewer[0].treated);
                    return (
                      <tr key={r.userId ?? r.name} className="border-t border-slate-100 text-slate-600">
                        <td className="py-2 pr-4 font-medium text-slate-800">{r.name}</td>
                        <td className="py-2 pr-4 font-bold text-slate-800">{r.treated}</td>
                        <td className="py-2 pr-4">{r.validated}</td>
                        <td className="py-2 pr-4">{r.rejected}</td>
                        <td className="py-2 pr-4">{r.escalated}</td>
                        <td className="py-2 pr-4">{r.sentToBuild}</td>
                        <td className="py-2">
                          <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
                            <div
                              className="h-full rounded-full bg-emerald-500"
                              style={{ width: `${Math.round((r.treated / top) * 100)}%` }}
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </GlassCard>

          {/* Ce que les chiffres veulent dire — pour éviter toute lecture ambiguë en réunion */}
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-[11px] leading-relaxed text-slate-500">
            <b className="text-slate-600">Comment lire ces chiffres.</b>{' '}
            « Traité » = un <b>verdict final</b> (Validée ou Rejetée) ; une escalade, un envoi vers Build
            ou une question à l'opérateur restent « en cours ». L'avancement global et la file sont
            l'<b>état actuel</b> (un dossier retravaillé n'est compté qu'une fois) ; les graphiques sont
            l'<b>activité datée</b> issue du journal d'audit (un dossier retravaillé compte à chaque fois).
            Périmètre : les tickets Uniticket « UT- » de type Address Management. Jours et semaines à
            l'heure de Bruxelles, semaines commençant le lundi. Données locales uniquement — aucun appel
            au DMP.
          </div>
        </>
      ) : null}
    </div>
  );
}
